import { computed, type MaybeRef, ref, unref } from 'vue';
import { z } from 'zod';

type SchemaErrors<T> = {
  [K in keyof T]?: T[K] extends object ? SchemaErrors<T[K]> : string;
}

export const useSchemaValidation = <T extends z.ZodTypeAny>(schema?: T, observe?: MaybeRef<unknown>, errorsOnlyWhenDirty = true) => {
  const values = computed(() => unref(observe) ?? {});
  const original = ref(JSON.stringify(values.value));

  const dirty = computed(() => JSON.stringify(values.value) !== original.value);

  const errors = computed<SchemaErrors<z.infer<typeof schema>>>(() => {
    if (!schema || !observe) return {};
    if (errorsOnlyWhenDirty && !dirty.value) return {};

    try {
      schema.parse(values.value);
      return {};
    } catch (e) {
      const result: object = {};
      (e as z.ZodError).issues?.forEach(issue => {
        result[issue.path[0]] = issue.message;
      });

      return result;
    }
  });

  const validate = <U extends z.ZodTypeAny = T>(inputSchema?: U, test?: MaybeRef<unknown>): [valid: boolean, errors?: Record<string, string[]>] => {
    const _schema = inputSchema ?? schema;
    const _test = test ?? values.value;

    if (!_test || !_schema) {
      throw new Error('No schema or values provided');
    }

    try {
      _schema.parse(_test);
      return [true];
    } catch (e) {
      const errs: Record<string, string[]> = {};

      (e as z.ZodError).issues?.forEach(issue => {
        const path = issue.path[0];
        if (path in errs) {
          errs[path].push(issue.message);
        } else {
          errs[path] = [issue.message];
        }
      });

      return [false, errs];
    }
  };

  const valid = computed(() => {
    const [isValid] = validate();
    return isValid;
  });

  const reset = (to?: MaybeRef<unknown>) => {
    const next = to !== undefined ? unref(to) : values.value;
    original.value = JSON.stringify(next);
  };

  return {
    dirty,
    errors,
    reset,
    valid,
    validate,
  };
};
